<template>
  <BasePopover v-bind="$attrs"
               v-on="$listeners"
               :disabled="disabled"
               :toggle="toggle"
               :style="popoverStyle">
    <template v-slot:trigger>
      <div ref="select-container"
           :class="$style.selectContainer"
           :disabled="disabled"
           :open="open"
           :style="selectContainerStyle">
        <div :class="$style.select"
             :disabled="disabled"
             :open="open"
             :style="selectStyle">
          <slot name="select" />
        </div>
        <div :class="$style.symbol"
             :disabled="disabled"
             :open="open"
             :style="expandSymbolStyle">
          <slot name="expand-symbol" />
        </div>
      </div>
    </template>
    <template v-slot:content>
      <div :class="$style.container"
           :disabled="disabled"
           :style="optionContainerStyle">
        <slot name="options-top" />
        <div v-for="(option, index) in options" :key="index"
             :class="$style.option"
             :disabled="!!option.disabled"
             :selected="!disableSelectState && (index === selectedOptionIndex || !!option.selected)"
             :style="optionStyle"
             @click="selectOption(index, option.disabled)">
          <template v-if="!disableSelectState && (index === selectedOptionIndex || !!option.selected)">
            <slot name="symbol-selected" />
          </template>
          <template v-else>
            <slot name="symbol-not-selected" />
          </template>
          {{ option.label ? option.label : option.name }}
        </div>
        <slot name="options-bottom" />
      </div>
    </template>
  </BasePopover>
</template>

<script>
  import BasePopover from '@base/base-popover'
  import { DefaultPseudoClassAttributeNames,
           DefaultPseudoClassVariableNames,
           getPseudoClassesObject,
           filterObjectByArray} from '@js/vue-style-utils'

  export default {
    name: 'BaseSelect',
    inheritAttrs: false,
    components: {
      BasePopover,
    },
    props: {
      allowDeselect: { type: Boolean, required: false, default: false },
      disabled: { type: Boolean, required: false, default: false },
      disableSelectState: { type: Boolean, required: false, default: false },
      initSelectedOptionIndex: { type: Number, required: false, default: null },
      toggle: { type: Boolean, required: false, default: false },
      open: { type: Boolean, required: false, default: false },
      options: { type: Array, required: true },
    },
    data: function () {
      return {
        checkSymbolStyle: {},
        expandSymbolStyle: {},
        optionStyle: {
          alignItems: 'center',
          display: 'flex',
        },
        popoverStyle: {},
        postfixes: ['', 'hover', 'active', 'selected', 'disabled', 'selected-hover',],
        selectedOptionIndex: null,
        selectContainerStyle: {
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
        },
        selectStyle: {},
        selectPostfixes: ['', 'hover', 'active', 'selected', 'disabled', 'open'],
      };
    },
    created: function () {
      this.selectedOptionIndex = this.initSelectedOptionIndex;

      let classObject = getPseudoClassesObject(
        this.$attrs.checkSymbolStyle, DefaultPseudoClassAttributeNames,
        '--base-select-check-symbol-', DefaultPseudoClassVariableNames,
        this.selectPostfixes);
      let attrs = filterObjectByArray(
        this.$attrs.checkSymbolStyle, DefaultPseudoClassAttributeNames);

      this.checkSymbolStyle = {
        ...this.checkSymbolStyle,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.$attrs.expandSymbolStyle, DefaultPseudoClassAttributeNames,
        '--base-select-expand-symbol-', DefaultPseudoClassVariableNames,
        this.selectPostfixes);
      attrs = filterObjectByArray(
        this.$attrs.expandSymbolStyle, DefaultPseudoClassAttributeNames);

      this.expandSymbolStyle = {
        ...this.expandSymbolStyle,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.$attrs.optionStyle, DefaultPseudoClassAttributeNames,
        '--base-select-option-', DefaultPseudoClassVariableNames,
        this.postfixes);
      attrs = filterObjectByArray(
        this.$attrs.optionStyle, DefaultPseudoClassAttributeNames);

      this.optionStyle = {
        ...this.optionStyle,
        ...attrs,
        ...classObject,
      };

      this.popoverStyle = {
        ...this.popoverStyle,
        ...this.$attrs,
      };

      classObject = getPseudoClassesObject(
        this.$attrs.selectContainerStyle, DefaultPseudoClassAttributeNames,
        '--base-select-select-container-', DefaultPseudoClassVariableNames,
        this.selectPostfixes);
      attrs = filterObjectByArray(
        this.$attrs.selectContainerStyle, DefaultPseudoClassAttributeNames);

      this.selectContainerStyle = {
        ...this.selectContainerStyle,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.$attrs.selectStyle, DefaultPseudoClassAttributeNames,
        '--base-select-select-', DefaultPseudoClassVariableNames,
        this.selectPostfixes);
      attrs = filterObjectByArray(
        this.$attrs.selectStyle, DefaultPseudoClassAttributeNames);

      this.selectStyle = {
        ...this.selectStyle,
        ...attrs,
        ...classObject,
      };
    },
    computed: {
      optionContainerStyle: function () {
        /* Do NOT move to data and created functions this. Otherwise,
           dropdown menu's width will differ from the one of select container.*/
        const classObject = getPseudoClassesObject(
          this.$attrs.optionContainerStyle, DefaultPseudoClassAttributeNames,
          '--base-select-option-container-', DefaultPseudoClassVariableNames,
          this.postfixes);
        const attrs = filterObjectByArray(
          this.$attrs.optionContainerStyle, DefaultPseudoClassAttributeNames);

        return {
          boxSizing: 'border-box',
          ...attrs,
          ...classObject,
        };
      },
    },
    methods: {
      selectOption: function (index, optionDisabled) {
        if (optionDisabled) {
          return;
        }

        if (this.allowDeselect && this.selectedOptionIndex === index) {
          this.selectedOptionIndex = null;
          this.$emit('select', null);
        } else {
          this.selectedOptionIndex = index;
          this.$emit('select', index);
        }
      },
    },
  };
</script>

<style lang="stylus" module>
  .selectContainer
    background-color var(--base-select-select-container-background-color)
    border var(--base-select-select-container-border)
    border-radius var(--base-select-select-container-border-radius)
    border-width var(--base-select-select-container-border-width)
    box-shadow var(--base-select-select-container-box-shadow)
    &:hover
      background-color var(--base-select-select-container-background-color-hover)
      border var(--base-select-select-container-border-hover)
      border-radius var(--base-select-select-container-border-radius-hover)
      border-width var(--base-select-select-container-border-width-hover)
      box-shadow var(--base-select-select-container-box-shadow-hover)
    &:active
      background-color var(--base-select-select-container-background-color-active)
      border var(--base-select-select-container-border-active)
      border-radius var(--base-select-select-container-border-radius-active)
      border-width var(--base-select-select-container-border-width-active)
      box-shadow var(--base-select-select-container-box-shadow-active)
    &[disabled]
      background-color var(--base-select-select-container-background-color-disabled)
      border var(--base-select-select-container-border-disabled)
      border-radius var(--base-select-select-container-border-radius-disabled)
      border-width var(--base-select-select-container-border-width-disabled)
      box-shadow var(--base-select-select-container-box-shadow-disabled)
    &[selected]
      background-color var(--base-select-select-container-background-color-selected)
      border var(--base-select-select-container-border-selected)
      border-radius var(--base-select-select-container-border-radius-selected)
      border-width var(--base-select-select-container-border-width-selected)
      box-shadow var(--base-select-select-container-box-shadow-selected)
    &[open]
      background-color var(--base-select-select-container-background-color-open)
      border var(--base-select-select-container-border-open)
      border-radius var(--base-select-select-container-border-radius-open)
      border-width var(--base-select-select-container-border-width-open)
      box-shadow var(--base-select-select-container-box-shadow-open)

  .select
    color var(--base-select-select-color)
    font-family var(--base-select-select-font-family)
    font-size var(--base-select-select-font-size)
    font-weight var(--base-select-select-font-weight)
    &:hover
      color var(--base-select-select-color-hover)
      font-family var(--base-select-select-font-family-hover)
      font-size var(--base-select-select-font-size-hover)
      font-weight var(--base-select-select-font-weight-hover)
    &:active
      color var(--base-select-select-color-active)
      font-family var(--base-select-select-font-family-active)
      font-size var(--base-select-select-font-size-active)
      font-weight var(--base-select-select-font-weight-active)
    &[disabled]
      color var(--base-select-select-color-disabled)
      font-family var(--base-select-select-font-family-disabled)
      font-size var(--base-select-select-font-size-disabled)
      font-weight var(--base-select-select-font-weight-disabled)
    &[selected]
      color var(--base-select-select-color-selected)
      font-family var(--base-select-select-font-family-selected)
      font-size var(--base-select-select-font-size-selected)
      font-weight var(--base-select-select-font-weight-selected)
    &[open]
      color var(--base-select-select-color-open)
      font-family var(--base-select-select-font-family-open)
      font-size var(--base-select-select-font-size-open)
      font-weight var(--base-select-select-font-weight-open)

  .container
    background-color var(--base-select-option-container-background-color)
    border var(--base-select-option-container-border)
    border-radius var(--base-select-option-container-border-radius)
    border-width var(--base-select-option-container-border-width)
    box-shadow var(--base-select-option-container-box-shadow)
    &:hover
      background-color var(--base-select-option-container-background-color-hover)
      border var(--base-select-option-container-border-hover)
      border-radius var(--base-select-option-container-border-radius-hover)
      border-width var(--base-select-option-container-border-width-hover)
      box-shadow var(--base-select-option-container-box-shadow-hover)
    &:active
      background-color var(--base-select-option-container-background-color-active)
      border var(--base-select-option-container-border-active)
      border-radius var(--base-select-option-container-border-radius-active)
      border-width var(--base-select-option-container-border-width-active)
      box-shadow var(--base-select-option-container-box-shadow-active)
    &[disabled]
      background-color var(--base-select-option-container-background-color-disabled)
      border var(--base-select-option-container-border-disabled)
      border-radius var(--base-select-option-container-border-radius-disabled)
      border-width var(--base-select-option-container-border-width-disabled)
      box-shadow var(--base-select-option-container-box-shadow-disabled)
    $[selected]
      background-color var(--base-select-option-container-background-color-selected)
      border var(--base-select-option-container-border-selected)
      border-radius var(--base-select-option-container-border-radius-selected)
      border-width var(--base-select-option-container-border-width-selected)
      box-shadow var(--base-select-option-container-box-shadow-selected)

  .option
    background-color var(--base-select-option-background-color)
    border var(--base-select-option-border)
    border-radius var(--base-select-option-border-radius)
    border-width var(--base-select-option-border-width)
    box-shadow var(--base-select-option-box-shadow)
    color var(--base-select-option-color)
    cursor var(--base-select-option-cursor)
    font-family var(--base-select-option-font-family)
    font-size var(--base-select-option-font-size)
    font-weight var(--base-select-option-font-weight)
    text-decoration var(--base-select-option-text-decoration)
    &:hover
      background-color var(--base-select-option-background-color-hover)
      border var(--base-select-option-border-hover)
      border-radius var(--base-select-option-border-radius-hover)
      border-width var(--base-select-option-border-width-hover)
      box-shadow var(--base-select-option-box-shadow-hover)
      color var(--base-select-option-color-hover)
      cursor var(--base-select-option-cursor-hover)
      font-family var(--base-select-option-font-family-hover)
      font-size var(--base-select-option-font-size-hover)
      font-weight var(--base-select-option-font-weight-hover)
      text-decoration var(--base-select-option-text-decoration-hover)
    &:active
      background-color var(--base-select-option-background-color-active)
      border var(--base-select-option-border-active)
      border-radius var(--base-select-option-border-radius-active)
      border-width var(--base-select-option-border-width-active)
      box-shadow var(--base-select-option-box-shadow-active)
      color var(--base-select-option-color-active)
      cursor var(--base-select-option-cursor-active)
      font-family var(--base-select-option-font-family-active)
      font-size var(--base-select-option-font-size-active)
      font-weight var(--base-select-option-font-weight-active)
      text-decoration var(--base-select-option-text-decoration-active)
    &[disabled]
      background-color var(--base-select-option-background-color-disabled)
      border var(--base-select-option-border-disabled)
      border-radius var(--base-select-option-border-radius-disabled)
      border-width var(--base-select-option-border-width-disabled)
      box-shadow var(--base-select-option-box-shadow-disabled)
      color var(--base-select-option-color-disabled)
      cursor var(--base-select-option-cursor-disabled)
      font-family var(--base-select-option-font-family-disabled)
      font-size var(--base-select-option-font-size-disabled)
      font-weight var(--base-select-option-font-weight-disabled)
      text-decoration var(--base-select-option-text-decoration-disabled)
    &[selected]
      background-color var(--base-select-option-background-color-selected)
      border var(--base-select-option-border-selected)
      border-radius var(--base-select-option-border-radius-selected)
      border-width var(--base-select-option-border-width-selected)
      box-shadow var(--base-select-option-box-shadow-selected)
      color var(--base-select-option-color-selected)
      cursor var(--base-select-option-cursor-selected)
      font-family var(--base-select-option-font-family-selected)
      font-size var(--base-select-option-font-size-selected)
      font-weight var(--base-select-option-font-weight-selected)
      text-decoration var(--base-select-option-text-decoration-selected)
    &[selected]:hover
      background-color var(--base-select-option-background-color-selected-hover)
      border var(--base-select-option-border-selected-hover)
      border-radius var(--base-select-option-border-radius-selected-hover)
      border-width var(--base-select-option-border-width-selected-hover)
      box-shadow var(--base-select-option-box-shadow-selected-hover)
      color var(--base-select-option-color-selected-hover)
      cursor var(--base-select-option-cursor-selected-hover)
      font-family var(--base-select-option-font-family-selected-hover)
      font-size var(--base-select-option-font-size-selected-hover)
      font-weight var(--base-select-option-font-weight-selected-hover)
      text-decoration var(--base-select-option-text-decoration-selected-hover)

  .symbol
    color var(--base-select-symbol-color)
    font-family var(--base-select-symbol-font-family)
    font-size var(--base-select-symbol-font-size)
    font-weight var(--base-select-symbol-font-weight)
    &:hover
      color var(--base-select-symbol-color-hover)
      font-family var(--base-select-symbol-font-family-hover)
      font-size var(--base-select-symbol-font-size-hover)
      font-weight var(--base-select-symbol-font-weight-hover)
    &:active
      color var(--base-select-symbol-color-active)
      font-family var(--base-select-symbol-font-family-active)
      font-size var(--base-select-symbol-font-size-active)
      font-weight var(--base-select-symbol-font-weight-active)
    &[disabled]
      color var(--base-select-symbol-color-disabled)
      font-family var(--base-select-symbol-font-family-disabled)
      font-size var(--base-select-symbol-font-size-disabled)
      font-weight var(--base-select-symbol-font-weight-disabled)
    &[selected]
      color var(--base-select-symbol-color-selected)
      font-family var(--base-select-symbol-font-family-selected)
      font-size var(--base-select-symbol-font-size-selected)
      font-weight var(--base-select-symbol-font-weight-selected)
</style>
